import type { ColumnDef } from '@/shared/types/table-filters.types';
import type { DateInfo, Experience, ExperienceStatus } from '../types/experience.types';
import { EXPERIENCE_STATUS_CONFIG } from '../types/experience.types';

const EXPERIENCE_STATUSES: ExperienceStatus[] = ['DRAFT', 'AWAITING', 'CONFIRMED', 'REJECTED'];

// Reuses the badge labels so the filter and the table never drift apart.
const EXPERIENCE_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  EXPERIENCE_STATUSES.map((status) => [status, EXPERIENCE_STATUS_CONFIG[status].label])
);

/** DateInfo is year + optional month; a range filter needs a real, comparable date. */
function toComparableDate(dateInfo: DateInfo | null | undefined): Date | null {
  if (!dateInfo) return null;
  return new Date(Date.UTC(dateInfo.year, (dateInfo.month ?? 1) - 1, 1));
}

function booleanColumn(
  key: string,
  label: string,
  accessor: (e: Experience) => boolean
): ColumnDef<Experience> {
  return { key, label, filterType: 'boolean', accessor, sortable: true };
}

/** Filterable/sortable experience fields, shared by the desktop table and the mobile cards. */
export const experienceTableColumns: ColumnDef<Experience>[] = [
  {
    key: 'companyName',
    label: 'Company',
    filterType: 'text',
    accessor: (experience) => experience.companyName,
    sortable: true,
  },
  {
    key: 'jobTitle',
    label: 'Job Title',
    filterType: 'text',
    accessor: (experience) => experience.jobTitle,
    sortable: true,
  },
  {
    key: 'startDate',
    label: 'Started',
    filterType: 'date-range',
    accessor: (experience) => toComparableDate(experience.startDate),
    sortable: true,
  },
  {
    key: 'endDate',
    label: 'Ended',
    filterType: 'date-range',
    accessor: (experience) => toComparableDate(experience.endDate),
    sortable: true,
  },
  booleanColumn('isOngoing', 'Ongoing', (experience) => experience.isOngoing),
  booleanColumn('isFullTime', 'Full-time', (experience) => experience.isFullTime),
  booleanColumn('isContractor', 'Contractor', (experience) => experience.isContractor),
  booleanColumn('continuous', 'Continuous', (experience) => experience.continuous),
  booleanColumn('isGig', 'Gig', (experience) => experience.isGig),
  {
    key: 'tags',
    label: 'Tags',
    filterType: 'text',
    accessor: (experience) => experience.tags,
    sortable: true,
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    accessor: (experience) => experience.status,
    options: EXPERIENCE_STATUSES,
    optionLabels: EXPERIENCE_STATUS_LABELS,
    sortable: true,
  },
  {
    key: 'createdAt',
    label: 'Created',
    filterType: 'date-range',
    accessor: (experience) => experience.createdAt,
    sortable: true,
  },
];
